"use strict";

/**
 * Image Zoom Lightbox for the Vertical Gallery
 * - Click main image to open fullscreen lightbox
 * - Zoom toggle button for 2x magnification (configurable)
 * - Prev/next navigation through visible (same-color) images
 * - Keyboard: Escape to close, Arrow keys to navigate
 * - Drag to pan when zoomed
 */

var ImageZoomLightbox = (function () {
  // --- Configurable ---
  var ZOOM_SCALE = 2; // Zoom multiplier (adjustable)

  // --- DOM refs ---
  var lightbox = null;
  var overlay = null;
  var closeBtn = null;
  var prevBtn = null;
  var nextBtn = null;
  var zoomToggle = null;
  var zoomInIcon = null;
  var zoomOutIcon = null;
  var imageWrapper = null;
  var image = null;
  var videoWrapper = null;

  // --- State ---
  var images = []; // Array of { src, highRes }
  var currentIndex = 0;
  var isZoomed = false;
  var isDragging = false;
  var dragStart = { x: 0, y: 0 };
  var scrollStart = { x: 0, y: 0 };

  function init() {
    lightbox = document.getElementById('image-zoom-lightbox');
    if (!lightbox) return;

    overlay = lightbox.querySelector('.izl__overlay');
    closeBtn = lightbox.querySelector('.izl__close');
    prevBtn = lightbox.querySelector('.izl__nav--prev');
    nextBtn = lightbox.querySelector('.izl__nav--next');
    zoomToggle = lightbox.querySelector('.izl__zoom-toggle');
    zoomInIcon = lightbox.querySelector('.izl__zoom-in-icon');
    zoomOutIcon = lightbox.querySelector('.izl__zoom-out-icon');
    imageWrapper = lightbox.querySelector('.izl__image-wrapper');
    image = lightbox.querySelector('.izl__image');
    videoWrapper = lightbox.querySelector('.izl__video-wrapper');

    // Event listeners
    overlay.addEventListener('click', close);
    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function () { navigate(-1); });
    nextBtn.addEventListener('click', function () { navigate(1); });
    zoomToggle.addEventListener('click', toggleZoom);
    image.addEventListener('click', handleImageClick);

    // Keyboard
    document.addEventListener('keydown', handleKeyDown);

    // Drag to pan when zoomed
    imageWrapper.addEventListener('mousedown', handleDragStart);
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);

    // Touch drag for mobile
    imageWrapper.addEventListener('touchstart', handleTouchStart, { passive: false });
    imageWrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    imageWrapper.addEventListener('touchend', handleTouchEnd);
  }

  /**
   * Open the lightbox
   * @param {Array<{src: string, highRes: string}>} imageList - visible images
   * @param {number} startIndex - which image to show first
   */
  function open(imageList, startIndex) {
    images = imageList || [];
    currentIndex = startIndex || 0;
    if (images.length === 0) return;

    showImage(currentIndex);
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    updateNavButtons();
  }

  function close() {
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    resetZoom();
    clearVideo();
    image.style.display = '';
    image.src = '';
    zoomToggle.style.display = '';
  }

  function showImage(index) {
    if (index < 0 || index >= images.length) return;
    currentIndex = index;
    resetZoom();
    clearVideo();

    var item = images[currentIndex];
    if (item.type === 'video' && item.videoHtml) {
      image.style.display = 'none';
      videoWrapper.innerHTML = item.videoHtml;
      videoWrapper.style.display = 'flex';
      zoomToggle.style.display = 'none';
      // Add playsinline to video elements
      var vid = videoWrapper.querySelector('video');
      if (vid) {
        vid.setAttribute('playsinline', '');
        vid.setAttribute('webkit-playsinline', '');
        vid.play();
      }
    } else {
      image.style.display = '';
      image.src = item.src;
      videoWrapper.style.display = 'none';
      zoomToggle.style.display = '';
      // Preload high-res for when user zooms
      if (item.highRes) {
        var preload = new Image();
        preload.src = item.highRes;
      }
    }
    updateNavButtons();
  }

  function clearVideo() {
    if (videoWrapper) {
      videoWrapper.innerHTML = '';
      videoWrapper.style.display = 'none';
    }
  }

  function navigate(direction) {
    var newIndex = currentIndex + direction;
    if (newIndex < 0 || newIndex >= images.length) return;
    showImage(newIndex);
  }

  function updateNavButtons() {
    if (images.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    } else {
      prevBtn.style.display = '';
      nextBtn.style.display = '';
      prevBtn.disabled = currentIndex === 0;
      nextBtn.disabled = currentIndex === images.length - 1;
    }
  }

  function toggleZoom() {
    if (isZoomed) {
      resetZoom();
    } else {
      applyZoom();
    }
  }

  function applyZoom() {
    isZoomed = true;
    imageWrapper.classList.add('izl--zoomed');
    zoomInIcon.style.display = 'none';
    zoomOutIcon.style.display = '';

    // Switch to high-res if available
    var item = images[currentIndex];
    if (item && item.highRes) {
      image.src = item.highRes;
    }

    // Set zoomed size: 2x the viewport-fitted dimensions
    var wrapperW = imageWrapper.clientWidth;
    var wrapperH = imageWrapper.clientHeight;
    var naturalW = image.naturalWidth || wrapperW;
    var naturalH = image.naturalHeight || wrapperH;

    // Calculate fit size first
    var fitScale = Math.min(wrapperW / naturalW, wrapperH / naturalH, 1);
    var fitW = naturalW * fitScale;
    var fitH = naturalH * fitScale;

    // Apply zoom
    var zoomedW = fitW * ZOOM_SCALE;
    var zoomedH = fitH * ZOOM_SCALE;

    image.style.width = zoomedW + 'px';
    image.style.height = zoomedH + 'px';
    image.style.maxWidth = 'none';
    image.style.maxHeight = 'none';

    // Center scroll
    requestAnimationFrame(function () {
      imageWrapper.scrollLeft = (zoomedW - wrapperW) / 2;
      imageWrapper.scrollTop = (zoomedH - wrapperH) / 2;
    });
  }

  function resetZoom() {
    isZoomed = false;
    isDragging = false;
    imageWrapper.classList.remove('izl--zoomed', 'izl--dragging');
    zoomInIcon.style.display = '';
    zoomOutIcon.style.display = 'none';
    image.style.width = '';
    image.style.height = '';
    image.style.maxWidth = '';
    image.style.maxHeight = '';
    imageWrapper.scrollLeft = 0;
    imageWrapper.scrollTop = 0;
  }

  function handleImageClick(e) {
    e.stopPropagation();
    toggleZoom();
  }

  function handleKeyDown(e) {
    if (lightbox.getAttribute('aria-hidden') !== 'false') return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      navigate(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      navigate(1);
    }
  }

  // --- Drag to pan (mouse) ---
  function handleDragStart(e) {
    if (!isZoomed) return;
    isDragging = true;
    imageWrapper.classList.add('izl--dragging');
    dragStart.x = e.clientX;
    dragStart.y = e.clientY;
    scrollStart.x = imageWrapper.scrollLeft;
    scrollStart.y = imageWrapper.scrollTop;
    e.preventDefault();
  }

  function handleDragMove(e) {
    if (!isDragging) return;
    var dx = e.clientX - dragStart.x;
    var dy = e.clientY - dragStart.y;
    imageWrapper.scrollLeft = scrollStart.x - dx;
    imageWrapper.scrollTop = scrollStart.y - dy;
  }

  function handleDragEnd() {
    if (!isDragging) return;
    isDragging = false;
    imageWrapper.classList.remove('izl--dragging');
  }

  // --- Touch drag (mobile) ---
  function handleTouchStart(e) {
    if (!isZoomed || e.touches.length !== 1) return;
    isDragging = true;
    imageWrapper.classList.add('izl--dragging');
    dragStart.x = e.touches[0].clientX;
    dragStart.y = e.touches[0].clientY;
    scrollStart.x = imageWrapper.scrollLeft;
    scrollStart.y = imageWrapper.scrollTop;
  }

  function handleTouchMove(e) {
    if (!isDragging || e.touches.length !== 1) return;
    e.preventDefault();
    var dx = e.touches[0].clientX - dragStart.x;
    var dy = e.touches[0].clientY - dragStart.y;
    imageWrapper.scrollLeft = scrollStart.x - dx;
    imageWrapper.scrollTop = scrollStart.y - dy;
  }

  function handleTouchEnd() {
    if (!isDragging) return;
    isDragging = false;
    imageWrapper.classList.remove('izl--dragging');
  }

  // Public API
  return {
    init: init,
    open: open,
    close: close
  };
})();

document.addEventListener('DOMContentLoaded', function () {
  ImageZoomLightbox.init();
});
